import { startCase } from 'lodash'
import { RecurringTransactionFrequency } from 'aven_types'

export const toFormattedUSD = (number: number, fractionDigits?: number): string => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        maximumFractionDigits: fractionDigits,
        // minimumFractionDigits is required to set maximumFractionDigits < 2
        minimumFractionDigits: fractionDigits,
    }).format(number)
}

// Kept consistent with backend: aven_backend/src/util/nameFormat.ts
export const toTitleCase = (str: string): string => {
    const titledCase = str.replace(/\w+/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase())
    return titledCase.replace(/\w'S(?=\s|$)/g, (txt) => txt.toLowerCase())
}

export const formatNumber = (number: number, fractionDigits?: number): string => {
    return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: fractionDigits,
        minimumFractionDigits: fractionDigits,
    }).format(number)
}

export const toFormattedUSDKMOrB = (number: number, includePlusForPositive = false, includeDecimalPlaces: boolean = true): string => {
    const absNumber = Math.abs(number)
    let formattedNumber: string
    let suffix: string = ''

    if (absNumber >= 1_000_000_000) {
        formattedNumber = formatNumber(absNumber / 1_000_000_000, includeDecimalPlaces ? 2 : 0)
        suffix = 'B'
    } else if (absNumber >= 1_000_000) {
        formattedNumber = formatNumber(absNumber / 1_000_000, includeDecimalPlaces ? 2 : 0)
        suffix = 'M'
    } else if (absNumber >= 1000) {
        formattedNumber = formatNumber(absNumber / 1000, includeDecimalPlaces ? 1 : 0)
        suffix = 'K'
    } else {
        formattedNumber = formatNumber(absNumber, 0)
    }

    let sign = ''
    if (number >= 0) {
        sign = includePlusForPositive ? '+' : ''
    } else {
        sign = '-'
    }
    return `${sign}$${formattedNumber}${suffix}`
}

export const toFormattedNumberKMOrB = (number: number, includeDecimalPlaces: boolean = true): string => {
    const absNumber = Math.abs(number)
    let formattedNumber: string
    let suffix: string = ''

    if (absNumber >= 1_000_000_000) {
        formattedNumber = formatNumber(number / 1_000_000_000, includeDecimalPlaces ? 2 : 0)
        suffix = 'B'
    } else if (absNumber >= 1_000_000) {
        formattedNumber = formatNumber(number / 1_000_000, includeDecimalPlaces ? 2 : 0)
        suffix = 'M'
    } else if (absNumber >= 1000) {
        formattedNumber = formatNumber(number / 1000, includeDecimalPlaces ? 1 : 0)
        suffix = 'K'
    } else {
        formattedNumber = formatNumber(number, 0)
    }

    return `${formattedNumber}${suffix}`
}

export const toFormattedPercent = (number: number, fractionDigits?: number, includePlusForPositive = false): string => {
    return `${includePlusForPositive && number > 0 ? '+' : ''}${new Intl.NumberFormat('en-US', {
        style: 'percent',
        maximumFractionDigits: fractionDigits,
    }).format(number)}`
}

export const formatFrequencyAsString = (frequency: RecurringTransactionFrequency): string => {
    switch (frequency) {
        case RecurringTransactionFrequency.Unknown:
            return ''
        case RecurringTransactionFrequency.Annually:
            return 'Yearly -'
        case RecurringTransactionFrequency.SemiMonthly:
            return 'Semi-Monthly -'
        default:
            return `${startCase(frequency.toLowerCase())} -`
    }
}

export const formatPhoneNumber = (phoneNumberString: string): string | null => {
    const cleaned = ('' + phoneNumberString).replace(/\D/g, '')
    const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
    if (match) {
        const intlCode = match[1] ? '+1 ' : ''
        return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('')
    }
    return null
}

export const formatAccountMask = (mask: string | null | undefined): string => {
    if (!mask) {
        return 'N/A'
    }

    return mask.startsWith('••••') ? mask : `••••${mask}`
}

type FormatRelativeDateOptions = {
    now?: Date
}

export const formatRelativeDate = (date: Date | string | number | null | undefined, options?: FormatRelativeDateOptions): string => {
    if (!date) {
        return 'N/A'
    }

    const snapshotDate = new Date(date)
    if (Number.isNaN(snapshotDate.getTime())) {
        return 'N/A'
    }

    const now = options?.now ?? new Date()
    const diffMs = now.getTime() - snapshotDate.getTime()
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMinutes = Math.floor(diffMs / (1000 * 60))

    if (diffDays > 0) {
        return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`
    }

    if (diffHours > 0) {
        return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`
    }

    if (diffMinutes > 0) {
        return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`
    }

    return 'Just now'
}

export const formatCrimeReportSourceString = (source: string): string => {
    return source
        .split('_') // Split the string by underscores
        .map(
            (word, index) =>
                index === 0
                    ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() // Capitalize the first word
                    : word.toLowerCase() // Make other words lowercase
        )
        .join(' ') // Join the words with spaces
}

export const toFormattedUSDStripTrailingZeroCents = (number: number): string => {
    let stringNum = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    }).format(number)

    stringNum = stringNum.replace(/\.00$/, '')

    return stringNum
}

export const toFormattedAprStripTrailingZerosAfterDecimalPoint = (number: number, fractionDigits = 2): string => {
    let stringNum = parseFloat(String(number * 100)).toFixed(fractionDigits)

    stringNum = stringNum.replace(/\.00$/, '')

    if (/\.\d0$/.test(stringNum)) {
        // match XX.X0 to drop the last 0
        return stringNum.slice(0, stringNum.length - 1) + '%'
    }
    return stringNum + '%'
}

export const formatRatioToPercentageStripTrailingZerosAndAtMostOneDecimalDigit = (ratioNumber: number): string => {
    let stringNum = parseFloat(String(ratioNumber * 100)).toFixed(1)
    stringNum = stringNum.replace(/\.0$/, '')
    return stringNum + '%'
}
